import cuid from "cuid";
import {
  defaultTo,
  defaultsDeep,
  filter,
  get,
  head,
  isBoolean,
  isEmpty,
  isNil,
  isNumber,
  isObject,
  keyBy,
  map,
} from "lodash";
import omitDeep from "omit-deep-lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Loading from "shared/components/Spin";
import {
  createFieldStore,
  FieldRegistryProvider,
} from "shared/hooks/useFieldRegistry";
import { useLazyQuery, useMutation, useQuery } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";

import { USER_AUTH } from "utils/api/graphql/queries/users";
import {
  ATTACHMENT_TYPES,
  BANK_ACCOUNT_OWNER_TYPE,
  CASE_TYPES,
  CONTACT_POINT_SYSTEM,
  JOB_LIST,
  MILTIS_PHONE_PREFIXES,
  MILTIS_PRODUCT_CODES,
  PAYMENT_RECEIVER,
  PROJECT_SOURCE,
  PROJECT_TYPES,
  RELATIONSHIP,
  SALE_MODES,
  SOCIO_PROFESSIONAL_CATEGORIES,
  SUBSCRIPTION_FORM_KEYS,
  SUBSCRIPTION_STATUSES,
} from "utils/constants";
import {
  getAgeFromBirthDate,
  normalizeMiltisChildRelatedPerson,
} from "utils/helpers/miltisRelatedPersonChild";
import View from "./View";
import { DOCUMENTS_TYPE } from "./Widgets/Proof";

export const STEPPER_KEYS = {
  ADDITIONAL_INFORMATION: "ADDITIONAL_INFORMATION",
  PROOF: "PROOF",
  DOCUMENT: "DOCUMENT",
};

const Detail = () => {
  const { id } = useParams();
  const fieldStore = useMemo(() => createFieldStore(), []);
  const [initialValues, setInitialValues] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checkUser] = useLazyQuery(USER_AUTH);
  const [updateSubscription] = useMutation(
    graphql.mutations.UPDATE_SUBSCRIPTION,
    {
      refetchQueries: [
        {
          query: graphql.queries.SUBSCRIPTION,
          awaitRefetchQueries: true,
          variables: { where: { id } },
        },
      ],
    },
  );

  const initSubscription = useCallback(({ subscription: data }) => {
    const subscription = omitDeep(data, "__typename");
    const subscriptionPayment = get(subscription, "payment");
    const paymentsTypes = get(
      subscription,
      "insurancePlan.fields.payment.type",
      [],
    );
    const debitDays = get(
      subscription,
      "insurancePlan.fields.payment.debitDay",
      [],
    );
    const frequencies = get(
      subscription,
      "insurancePlan.fields.payment.frequency",
      [],
    );
    const shouldBeAdherent =
      get(subscription, "payment.payer.owner.isAdherent", true) ||
      get(subscription, "insurancePlan.fields.payment.shouldBeAdherent", false);
    if (isEmpty(subscription)) return;
    const contactTelecoms = get(subscription, "project.contact.telecoms", []);
    const payerTelecoms = get(subscription, "payment.payer.owner.telecoms", []);
    const receiverTelecoms = get(
      subscription,
      "payment.receiver.owner.telecoms",
      [],
    );
    // Check if payer and receiver are the same account
    const isSameAccount =
      get(subscription, "payment.payer.id", "") ===
      get(subscription, "payment.receiver.id");
    const isLumineis =
      get(subscription, "insurancePlan.product") ===
      MILTIS_PRODUCT_CODES.LUMINEIS;
    const selfOccupationDisabled =
      get(subscription, "project.fields.selfOccupation") !== false;

    const initialDisabled = {
      project: {
        ria: true,
        contract: {
          caseType: get(subscription, "project.ria", false),
          saleMode:
            get(subscription, "project.fields.twoTimesSale", false) ||
            get(subscription, "project.fields.source") ===
              PROJECT_SOURCE.CLIENT,
        },
        contact: {
          siretNumber: true,
          optMadelin: !get(subscription, "insurancePlan.fields.madelin"),
          fields: {
            affiliateOrganizationCode: true,
          },
          profession: get(
            subscription,
            "insurancePlan.config.disabled",
            [],
          ).includes("project.contact.profession"),
        },
        projectType:
          get(subscription, "project.fields.source") ===
            PROJECT_SOURCE.CLIENT ||
          get(subscription, "project.fields.twoTimesSale") ||
          [SALE_MODES.FACE_TO_FACE].includes(
            get(subscription, "project.contract.saleMode"),
          ),
        terminationRequest: isLumineis
          ? {
              contractNumber: selfOccupationDisabled,
              address: selfOccupationDisabled,
              zipCode: selfOccupationDisabled,
              city: selfOccupationDisabled,
              email: selfOccupationDisabled,
              brokerageMandate: {
                currentCompany: selfOccupationDisabled,
              },
            }
          : {
              recipient: selfOccupationDisabled,
              dueDate: selfOccupationDisabled,
              contractNumber: selfOccupationDisabled,
            },
      },
      createdDate: true,
      proof: {
        cin: !get(subscription, "payment.payer.owner.isAdherent", true),
        // payerCin disabled when payer is adherent (no third-party payer)
        payerCin: get(subscription, "payment.payer.owner.isAdherent", true),
        // refundAccount disabled when payer and receiver are the same account
        refundAccount: isSameAccount,
      },
      // Disable receiver payment fields when same account (values come from payer)
      payment: {
        receiver: {
          owner: {
            firstName: isSameAccount,
            lastName: isSameAccount,
          },
          IBAN: isSameAccount,
          BIC: isSameAccount,
        },
      },
      telecoms: {
        payer: {
          lastName: shouldBeAdherent,
          firstName: shouldBeAdherent,
          [CONTACT_POINT_SYSTEM.STREET]: {
            value:
              shouldBeAdherent &&
              !get(subscription, "insurancePlan.fields.tns"),
          },
          [CONTACT_POINT_SYSTEM.ZIP_CODE]: {
            value:
              shouldBeAdherent &&
              !get(subscription, "insurancePlan.fields.tns"),
          },
          [CONTACT_POINT_SYSTEM.CITY]: {
            value:
              shouldBeAdherent &&
              !get(subscription, "insurancePlan.fields.tns"),
          },
        },
        // Disable receiver telecoms when same account (values come from payer)
        receiver: {
          [CONTACT_POINT_SYSTEM.ADDRESS]: {
            value: isSameAccount,
          },
          [CONTACT_POINT_SYSTEM.ZIP_CODE]: {
            value: isSameAccount,
          },
          [CONTACT_POINT_SYSTEM.CITY]: {
            value: isSameAccount,
          },
        },
        contact: {},
      },
      [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.TERMINATION_REQUEST]: !get(
        subscription,
        "project.ria",
        false,
      ),
      // [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.BROKERAGE_MANDATE]: !get(
      //   subscription,
      //   "project.ria",
      //   false
      // ),
      [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.PARTNER]: !get(
        subscription,
        "project.contact.relatedPersons",
        [],
      ).find((person) => person.relationship === RELATIONSHIP.SPOUSE),
      [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CHILDREN]: !get(
        subscription,
        "project.contact.relatedPersons",
        [],
      ).find((person) => person.relationship === RELATIONSHIP.CHILD),
      [SUBSCRIPTION_FORM_KEYS.PROOF.KBIS]: !get(
        subscription,
        "insurancePlan.fields.tns",
      ),
      [SUBSCRIPTION_FORM_KEYS.PROOF.SOCIAL_SECURITY_CERTIFICATE]: !get(
        subscription,
        "project.contact.fields.teletransmission",
        true,
      ),
      [SUBSCRIPTION_FORM_KEYS.PROOF.PENSION_FUND]: !(
        (
          get(subscription, "insurancePlan.config.additionalDocuments", []) ||
          []
        ).includes("proof.pensionFund") &&
        get(subscription, "project.contact.socioProfessionalCategory") ===
          SOCIO_PROFESSIONAL_CATEGORIES.RETIREES
      ),
      [SUBSCRIPTION_FORM_KEYS.PROOF.COMMUNITY_ATTACHMENT]: !(
        (
          get(subscription, "insurancePlan.config.additionalDocuments", []) ||
          []
        ).includes("proof.attachmentOfCommunity") &&
        get(subscription, "project.contact.socioProfessionalCategory") !==
          SOCIO_PROFESSIONAL_CATEGORIES.RETIREES
      ),
      // Territorial Collectivity Attestation - disabled when NOT (isActive && isPublicServiceEmployee)
      [SUBSCRIPTION_FORM_KEYS.PROOF.TERRITORIAL_COLLECTIVITY_ATTESTATION]: !(
        get(subscription, "project.contact.fields.professionalSituation") ===
          JOB_LIST.ACTIVE &&
        get(subscription, "project.contact.profession") ===
          SOCIO_PROFESSIONAL_CATEGORIES.EXECUTIVES_AND_PUBLIC_SERVICE_EMPLOYEES
      ),
      // CNRACL/IRCANTEC Affiliation - disabled when NOT (isRetiredProfessional && isPublicServiceEmployee)
      [SUBSCRIPTION_FORM_KEYS.PROOF.CNRACL_IRCANTEC_AFFILIATION]: !(
        get(subscription, "project.contact.fields.professionalSituation") ===
          JOB_LIST.RETIRED &&
        get(subscription, "project.contact.profession") ===
          SOCIO_PROFESSIONAL_CATEGORIES.EXECUTIVES_AND_PUBLIC_SERVICE_EMPLOYEES
      ),
      // Territorial Collectivity Attestation - disabled when NOT (isActive && isPublicServiceEmployee)
      [SUBSCRIPTION_FORM_KEYS.PROOF.TERRITORIAL_COLLECTIVITY_ATTESTATION]: !(
        get(subscription, "project.contact.fields.professionalSituation") ===
          JOB_LIST.ACTIVE &&
        get(subscription, "project.contact.profession") ===
          SOCIO_PROFESSIONAL_CATEGORIES.EXECUTIVES_AND_PUBLIC_SERVICE_EMPLOYEES
      ),
      // CNRACL/IRCANTEC Affiliation - disabled when NOT (isRetiredProfessional && isPublicServiceEmployee)
      [SUBSCRIPTION_FORM_KEYS.PROOF.CNRACL_IRCANTEC_AFFILIATION]: !(
        get(subscription, "project.contact.fields.professionalSituation") ===
          JOB_LIST.RETIRED &&
        get(subscription, "project.contact.profession") ===
          SOCIO_PROFESSIONAL_CATEGORIES.EXECUTIVES_AND_PUBLIC_SERVICE_EMPLOYEES
      ),
      // Territorial Collectivity Attestation - disabled when NOT (isActive && isPublicServiceEmployee)
      [SUBSCRIPTION_FORM_KEYS.PROOF.TERRITORIAL_COLLECTIVITY_ATTESTATION]: !(
        get(subscription, "project.contact.fields.professionalSituation") ===
          JOB_LIST.ACTIVE &&
        get(subscription, "project.contact.profession") ===
          SOCIO_PROFESSIONAL_CATEGORIES.EXECUTIVES_AND_PUBLIC_SERVICE_EMPLOYEES
      ),
      // CNRACL/IRCANTEC Affiliation - disabled when NOT (isRetiredProfessional && isPublicServiceEmployee)
      [SUBSCRIPTION_FORM_KEYS.PROOF.CNRACL_IRCANTEC_AFFILIATION]: !(
        get(subscription, "project.contact.fields.professionalSituation") ===
          JOB_LIST.RETIRED &&
        get(subscription, "project.contact.profession") ===
          SOCIO_PROFESSIONAL_CATEGORIES.EXECUTIVES_AND_PUBLIC_SERVICE_EMPLOYEES
      ),
      // Proof of Address - never disabled
      [SUBSCRIPTION_FORM_KEYS.PROOF.PROOF_OF_ADDRESS]: false,
      // Payer RIB - disabled when NOT two separate RIBs
      [SUBSCRIPTION_FORM_KEYS.PROOF.PAYER_RIB]:
        get(subscription, "payment.payer.id", "") ===
        get(subscription, "payment.receiver.id"),
      // Receiver RIB - disabled when NOT two separate RIBs
      [SUBSCRIPTION_FORM_KEYS.PROOF.RECEIVER_RIB]:
        get(subscription, "payment.payer.id", "") ===
        get(subscription, "payment.receiver.id"),
    };

    const telecoms = defaultsDeep(
      {
        contact: keyBy(contactTelecoms, "system"),
        payer: keyBy(
          shouldBeAdherent ? contactTelecoms : payerTelecoms,
          "system",
        ),
        receiver: keyBy(receiverTelecoms, "system"),
      },
      {
        contact: {
          // [CONTACT_POINT_SYSTEM.ADDRESS_NUMBER]: { value: null },
          // [CONTACT_POINT_SYSTEM.REPETITION_INDEX]: { value: null },
          // [CONTACT_POINT_SYSTEM.STREET]: { value: null },
          [CONTACT_POINT_SYSTEM.ZIP_CODE]: { value: null },
          [CONTACT_POINT_SYSTEM.CITY]: { value: null },
          [CONTACT_POINT_SYSTEM.PHONE]: { value: null },
          [CONTACT_POINT_SYSTEM.PHONE_NUMBER_PREFIX]: {
            value: MILTIS_PHONE_PREFIXES[0].value,
          },
          [CONTACT_POINT_SYSTEM.LANDLINE]: { value: null },
          [CONTACT_POINT_SYSTEM.EMAIL]: { value: null },
        },
        payer: {
          // [CONTACT_POINT_SYSTEM.ADDRESS_NUMBER]: { value: null },
          // [CONTACT_POINT_SYSTEM.REPETITION_INDEX]: { value: null },
          [CONTACT_POINT_SYSTEM.STREET]: { value: null },
          [CONTACT_POINT_SYSTEM.ZIP_CODE]: { value: null },
          [CONTACT_POINT_SYSTEM.CITY]: { value: null },
        },
      },
    );
    const children = filter(
      get(subscription, "project.contact.relatedPersons", []),
      {
        relationship: RELATIONSHIP.CHILD,
      },
    ).map((child) => normalizeMiltisChildRelatedPerson(child));

    // Determine payment conditions for document requirements
    const payment = get(subscription, "payment");
    const receiverId = get(payment, "receiver.id");
    const payerId = get(payment, "payer.id");
    const isPayerReceiver = receiverId === payerId;
    const payerIsAdherent = get(payment, "payer.owner.isAdherent", true);
    const receiverIsAdherent = get(payment, "receiver.owner.isAdherent", true);

    const requiresSubscriberPaysButDifferentReceiver =
      payerIsAdherent && !receiverIsAdherent && !isPayerReceiver;
    const requiresThirdPartyPayer = !payerIsAdherent;
    const requiresBothThirdParty =
      !payerIsAdherent && !receiverIsAdherent && !isPayerReceiver;
    const requiresTwoSeparateRibs = !isPayerReceiver;

    const showAdherentCin =
      requiresSubscriberPaysButDifferentReceiver ||
      requiresThirdPartyPayer ||
      requiresBothThirdParty;

    // Check for spouse
    const hasSpouse = !isEmpty(
      get(subscription, "project.contact.relatedPersons", []).find(
        (person) => person?.relationship === RELATIONSHIP.SPOUSE,
      ),
    );

    // Conditions for proof documents
    const acceptTeletransmission = get(
      subscription,
      "project.contact.fields.teletransmission",
      true,
    );
    const isTNS = get(subscription, "insurancePlan.fields.tns");
    const isRetired =
      get(subscription, "project.contact.socioProfessionalCategory") ===
      SOCIO_PROFESSIONAL_CATEGORIES.RETIREES;
    const additionalDocuments =
      get(subscription, "insurancePlan.config.additionalDocuments", []) || [];
    const isPensionFundRequired =
      additionalDocuments.includes("proof.pensionFund") && isRetired;
    const isCommunityDocumentRequired =
      additionalDocuments.includes("proof.attachmentOfCommunity") && !isRetired;

    // Determine professional situation conditions
    const professionalSituation = get(
      subscription,
      "project.contact.fields.professionalSituation",
    );
    const profession = get(subscription, "project.contact.profession");
    const isPublicServiceEmployee =
      profession ===
      SOCIO_PROFESSIONAL_CATEGORIES.EXECUTIVES_AND_PUBLIC_SERVICE_EMPLOYEES;
    const isActive = professionalSituation === JOB_LIST.ACTIVE;
    const isRetiredProfessional = professionalSituation === JOB_LIST.RETIRED;
    const requiresTerritorialCollectivityAttestation =
      isActive && isPublicServiceEmployee;
    const requiresCnraclIrcantecAffiliation =
      isRetiredProfessional && isPublicServiceEmployee;

    const fields = {
      [STEPPER_KEYS.ADDITIONAL_INFORMATION]: {
        [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CONTRACT]: [
          "project.contract.issuanceDate",
          "project.contract.caseType",
          // "project.ria",
          "project.contract.saleMode",
          "project.projectType",
          "createdDate",
        ],
        [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.TERMINATION_REQUEST]:
          isLumineis
            ? [
                "project.terminationRequest.contractNumber",
                "project.terminationRequest.brokerageMandate.currentCompany",
                "project.terminationRequest.address",
                "project.terminationRequest.zipCode",
                "project.terminationRequest.city",
                "project.terminationRequest.email",
              ]
            : [
                "project.terminationRequest.contractNumber",
                "project.terminationRequest.recipient",
                "project.terminationRequest.dueDate",
              ],
        // [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.BROKERAGE_MANDATE]: [
        // "project.terminationRequest.brokerageMandate.lastName",
        // "project.terminationRequest.brokerageMandate.firstName",
        // "project.terminationRequest.brokerageMandate.currentCompany",
        // ],
        [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.ADHERENT]: [
          "project.contact.user.gender",
          "project.contact.user.lastname",
          "project.contact.user.firstname",
          "project.contact.user.birthDate",
          "project.contact.socialRegime",
          "project.contact.socialSecurityNumber",
          "project.contact.fields.affiliateOrganizationCode",
          `telecoms.contact.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`,
          `telecoms.contact.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`,
          // ...(isLumineis
          //   ? [
          //       `telecoms.contact.${CONTACT_POINT_SYSTEM.BIRTH_CITY}.value`,
          //       `telecoms.contact.${CONTACT_POINT_SYSTEM.BIRTH_ZIP_CODE}.value`,
          //     ]
          //   : []),
          "project.contact.familySituation",
        ],
        [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.ACTIVITY]: [
          // "project.contact.politicallyExposedPerson",
          // "project.contact.exerciseFrame",
          // "project.contact.siretNumber",
          "project.contact.profession",
          get(subscription, "insurancePlan.product") ===
          MILTIS_PRODUCT_CODES.AURA_TER
            ? "project.contact.fields.professionalSituation"
            : null,
          // "project.contact.optMadelin",
        ],
        [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CONTACT]: [
          // `telecoms.contact.${CONTACT_POINT_SYSTEM.ADDRESS_NUMBER}.value`,
          // `telecoms.contact.${CONTACT_POINT_SYSTEM.REPETITION_INDEX}.value`,
          `telecoms.contact.${CONTACT_POINT_SYSTEM.ADDRESS}.value`,
          // `telecoms.contact.${CONTACT_POINT_SYSTEM.STREET_TYPE}.value`,
          `telecoms.contact.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
          `telecoms.contact.${CONTACT_POINT_SYSTEM.CITY}.value`,
          `telecoms.contact.${CONTACT_POINT_SYSTEM.PHONE}.value`,
          `telecoms.contact.${CONTACT_POINT_SYSTEM.EMAIL}.value`,
          // "project.contact.acceptContact",
          // "project.contact.acceptDigital",
        ],
        [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.PARTNER]: [
          "spouse.identity.user.gender",
          "spouse.identity.user.firstname",
          "spouse.identity.user.lastname",
          "spouse.identity.user.birthDate",
          // "spouse.identity.socioProfessionalCategory",
          "spouse.identity.socialRegime",
          "spouse.identity.socialSecurityNumber",
        ],
        [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CHILDREN]:
          children.flatMap((child, i) => {
            const paths = [
              `children.${i}.identity.user.gender`,
              `children.${i}.identity.user.firstname`,
              `children.${i}.identity.user.lastname`,
              `children.${i}.identity.user.birthDate`,
              `children.${i}.identity.socialRegime`,
              `children.${i}.identity.socialSecurityNumber`,
            ];
            if (getAgeFromBirthDate(child?.identity?.user?.birthDate) <= 18) {
              paths.push(`children.${i}.authority`);
            }
            return paths;
          }),
        [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.PAYMENT]: [
          "payment.type",
          "payment.frequency",
          "payment.debitDay",
          "payment.payer.IBAN",
          "payment.payer.BIC",
          "payment.payer.owner.firstName",
          "payment.payer.owner.lastName",
          // `telecoms.payer.${CONTACT_POINT_SYSTEM.ADDRESS}.value`,
          // `telecoms.payer.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
          // `telecoms.payer.${CONTACT_POINT_SYSTEM.CITY}.value`,
        ],
        [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.REFUND]: [
          "isPayerReceiver",
          "payment.receiver.owner.firstName",
          "payment.receiver.owner.lastName",
          "payment.receiver.IBAN",
          "payment.receiver.BIC",
          `telecoms.receiver.${CONTACT_POINT_SYSTEM.ADDRESS}.value`,
          `telecoms.receiver.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
          `telecoms.receiver.${CONTACT_POINT_SYSTEM.CITY}.value`,
        ],
        [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.INFORMATION]: [
          // "project.fields.isPPE",
          // "project.fields.ppeFunction",
          // "project.fields.ppeRelated",
          "project.fields.acceptProductStatuses",
          "project.fields.acceptAgreement",
          "project.fields.acceptFees",
          "project.fields.phoneCanvassingConsent",
          // "project.fields.info.justifications",
          // "project.fields.info.comment",
        ],
      },
      [STEPPER_KEYS.PROOF]: {
        // Social Security Certificate - only if teletransmission accepted
        ...(acceptTeletransmission && {
          [SUBSCRIPTION_FORM_KEYS.PROOF.SOCIAL_SECURITY_CERTIFICATE]: [
            "proof.socialSecurityCertificate",
            ...(hasSpouse ? ["proof.spouseSocialSecurityCertificate"] : []),
            ...children.map((child, index) => {
              const userId = get(child, "identity.user.id");
              return userId
                ? `proof.childSocialSecurityCertificate_${userId}`
                : `proof.childSocialSecurityCertificate_${index}`;
            }),
          ],
        }),
        // ID documents - conditionally required based on payment conditions
        [SUBSCRIPTION_FORM_KEYS.PROOF.ID]: [
          ...(showAdherentCin ? ["proof.cin"] : []),
          ...(requiresThirdPartyPayer || requiresBothThirdParty
            ? ["proof.payerCin"]
            : []),
          ...(requiresSubscriberPaysButDifferentReceiver ||
          requiresBothThirdParty
            ? ["proof.receiverCin"]
            : []),
        ],
        // Bank Account - handles single RIB or dual RIBs based on payment conditions
        [SUBSCRIPTION_FORM_KEYS.PROOF.BANK_ACCOUNT]: requiresTwoSeparateRibs
          ? ["proof.paymentAccount", "proof.refundAccount"]
          : ["proof.paymentAccount"],
        // Proof of Address - not requested for LUMINEIS
        ...(!isLumineis && {
          [SUBSCRIPTION_FORM_KEYS.PROOF.PROOF_OF_ADDRESS]: [
            "proof.proofOfAddress",
          ],
        }),
        // KBIS - only for TNS
        // ...(isTNS && {
        //   [SUBSCRIPTION_FORM_KEYS.PROOF.KBIS]: ["proof.kbis"],
        // }),
        // Pension Fund - only when required (retirees + additionalDocuments config)
        ...(isPensionFundRequired && {
          [SUBSCRIPTION_FORM_KEYS.PROOF.PENSION_FUND]: ["proof.pensionFund"],
        }),
        // Community Attachment - only when required
        ...(isCommunityDocumentRequired && {
          [SUBSCRIPTION_FORM_KEYS.PROOF.COMMUNITY_ATTACHMENT]: [
            "proof.attachmentOfCommunity",
          ],
        }),
        // Territorial Collectivity Attestation - required when "En activité" AND profession is public service
        ...(requiresTerritorialCollectivityAttestation && {
          [SUBSCRIPTION_FORM_KEYS.PROOF.TERRITORIAL_COLLECTIVITY_ATTESTATION]: [
            "proof.territorialCollectivityAttestation",
          ],
        }),
        // CNRACL/IRCANTEC Affiliation - required when "Retraité" AND profession is public service
        ...(requiresCnraclIrcantecAffiliation && {
          [SUBSCRIPTION_FORM_KEYS.PROOF.CNRACL_IRCANTEC_AFFILIATION]: [
            "proof.cnraclIrcantecAffiliation",
          ],
        }),
      },
    };

    const spouse = get(subscription, "project.contact.relatedPersons", []).find(
      ({ relationship }) => relationship === RELATIONSHIP.SPOUSE,
    );

    const additionalInfoInitialValues = {
      ...subscription,
      telecoms,
      project: {
        ...subscription.project,
        fields: {
          ...get(subscription, "project.fields", {}),
        },
        ...(subscription.project.fields.twoTimesSale && {
          projectType: PROJECT_TYPES.COLD_LEAD,
        }),
        contact: defaultsDeep(subscription.project.contact, {
          fields: { teletransmission: true, familySituation: null },
        }),
        contract: {
          ...subscription.project.contract,
          ...(subscription.project.ria && {
            caseType: CASE_TYPES.RETURN_TO_COMPETITION,
          }),
          ...(subscription.project.fields.twoTimesSale && {
            saleMode: SALE_MODES.DISTANT,
          }),
        },
        ...(subscription.project.ria
          ? {
              terminationRequest: {
                id: cuid(),
                ...subscription.project.terminationRequest,
                brokerageMandate: {
                  id: cuid(),
                  firstName: get(
                    subscription,
                    "project.contact.user.firstname",
                  ),
                  lastName: get(subscription, "project.contact.user.lastname"),
                  ...subscription.project.terminationRequest?.brokerageMandate,
                },
              },
            }
          : {}),
      },
      isPayerReceiver:
        get(subscription, "payment.payer.id", "") ===
        get(subscription, "payment.receiver.id")
          ? PAYMENT_RECEIVER.SAME_ACCOUNT
          : PAYMENT_RECEIVER.ANOTHER_ACCOUNT,
      payment: {
        id: cuid(),
        ...subscriptionPayment,
        type: get(
          subscriptionPayment,
          "type",
          paymentsTypes.length === 1 ? head(paymentsTypes) : null,
        ),
        debitDay: get(
          subscriptionPayment,
          "debitDay",
          debitDays.length === 1 ? head(debitDays) : null,
        ),
        frequency: get(
          subscriptionPayment,
          "frequency",
          frequencies.length === 1 ? head(frequencies) : null,
        ),
        payer: {
          id: cuid(),
          IBAN: null,
          BIC: null,
          ...subscription.payment?.payer,
          owner: {
            id: cuid(),
            ...subscription.payment?.payer?.owner,
            firstName: defaultTo(
              subscription.payment?.payer?.owner?.firstName,
              shouldBeAdherent
                ? get(subscription, "project.contact.user.firstname")
                : null,
            ),
            lastName: defaultTo(
              subscription.payment?.payer?.owner?.lastName,
              shouldBeAdherent
                ? get(subscription, "project.contact.user.lastname")
                : null,
            ),
            isAdherent: defaultTo(
              subscription.payment?.payer?.owner?.isAdherent,
              shouldBeAdherent,
            ),
            type: get(subscription, "insurancePlan.fields.tns")
              ? BANK_ACCOUNT_OWNER_TYPE.COMPANY
              : BANK_ACCOUNT_OWNER_TYPE.PARTICULAR,
          },
        },
        receiver: defaultsDeep(subscription.payment?.receiver, {
          id: cuid(),
          IBAN: null,
          BIC: null,
          owner: { id: cuid() },
        }),
      },
      children,
      spouse: isEmpty(spouse)
        ? undefined
        : defaultsDeep(spouse, {
            identity: { fields: { teletransmission: true } },
          }),
    };

    // Build proof initial values from attachments
    const attachments = get(subscription, "attachments", []);

    // Get user IDs for filtering
    const adherentUserId = get(subscription, "project.contact.user.id");
    const spouseData = get(
      subscription,
      "project.contact.relatedPersons",
      [],
    ).find((person) => person?.relationship === RELATIONSHIP.SPOUSE);
    const spouseUserId = get(spouseData, "identity.user.id");
    const payerUserId = get(subscription, "payment.payer.owner.user.id");
    const receiverUserId = get(subscription, "payment.receiver.owner.user.id");

    // Helper to filter attachments by type and user
    const filterAttachmentsByTypeAndUser = (type, userId) =>
      map(
        filter(attachments, (att) => {
          const attUserId = get(att, "user.id") || get(att, "userId");
          return att.type === type && attUserId === userId;
        }),
        ({ id, name, fileUrl }) => ({
          uid: id,
          name,
          status: "done",
          url: fileUrl,
        }),
      );

    // Map each document type to the correct user
    const proofDocuments = {
      // Adherent documents
      cin: filterAttachmentsByTypeAndUser(DOCUMENTS_TYPE.cin, adherentUserId),
      socialSecurityCertificate: filterAttachmentsByTypeAndUser(
        DOCUMENTS_TYPE.socialSecurityCertificate,
        adherentUserId,
      ),
      proofOfAddress: filterAttachmentsByTypeAndUser(
        DOCUMENTS_TYPE.proofOfAddress,
        adherentUserId,
      ),
      paymentAccount: filterAttachmentsByTypeAndUser(
        DOCUMENTS_TYPE.paymentAccount,
        adherentUserId,
      ),
      kbis: filterAttachmentsByTypeAndUser(DOCUMENTS_TYPE.kbis, adherentUserId),
      pensionFund: filterAttachmentsByTypeAndUser(
        DOCUMENTS_TYPE.pensionFund,
        adherentUserId,
      ),
      attachmentOfCommunity: filterAttachmentsByTypeAndUser(
        DOCUMENTS_TYPE.attachmentOfCommunity,
        adherentUserId,
      ),
      territorialCollectivityAttestation: filterAttachmentsByTypeAndUser(
        DOCUMENTS_TYPE.territorialCollectivityAttestation,
        adherentUserId,
      ),
      cnraclIrcantecAffiliation: filterAttachmentsByTypeAndUser(
        DOCUMENTS_TYPE.cnraclIrcantecAffiliation,
        adherentUserId,
      ),
      // Payer documents (if different from adherent)
      payerCin: filterAttachmentsByTypeAndUser(
        DOCUMENTS_TYPE.payerCin,
        payerUserId || adherentUserId,
      ),
      // Receiver documents (if different from adherent)
      receiverCin: filterAttachmentsByTypeAndUser(
        DOCUMENTS_TYPE.receiverCin,
        receiverUserId || adherentUserId,
      ),
      refundAccount: filterAttachmentsByTypeAndUser(
        DOCUMENTS_TYPE.refundAccount,
        receiverUserId || adherentUserId,
      ),
      // Spouse documents
      spouseSocialSecurityCertificate: spouseUserId
        ? filterAttachmentsByTypeAndUser(
            DOCUMENTS_TYPE.spouseSocialSecurityCertificate,
            spouseUserId,
          )
        : [],
    };

    // Add children attestation documents dynamically
    children.forEach((child, index) => {
      const userId = get(child, "identity.user.id");
      const fieldKey = userId
        ? `childSocialSecurityCertificate_${userId}`
        : `childSocialSecurityCertificate_${index}`;

      proofDocuments[fieldKey] = filterAttachmentsByTypeAndUser(
        ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE,
        userId,
      );
    });

    const proofInitialValues = {
      proof: proofDocuments,
    };
    setDisabled(initialDisabled);
    setInitialValues({
      [STEPPER_KEYS.ADDITIONAL_INFORMATION]: additionalInfoInitialValues,
      [STEPPER_KEYS.PROOF]: proofInitialValues,
      fields,
    });

    if (subscription.status === SUBSCRIPTION_STATUSES.SIGNED)
      setActive(STEPPER_KEYS.DOCUMENT);
    if (subscription.status === SUBSCRIPTION_STATUSES.PENDING)
      setActive(STEPPER_KEYS.DOCUMENT);
  }, []);

  const { data, loading, error } = useQuery(graphql.queries.SUBSCRIPTION, {
    variables: { where: { id } },
    onCompleted: initSubscription,
  });

  const [active, setActive] = useState(STEPPER_KEYS.ADDITIONAL_INFORMATION);
  const [disabled, setDisabled] = useState({});
  const [realProgress, setRealProgress] = useState({
    [STEPPER_KEYS.ADDITIONAL_INFORMATION]: 0,
    [STEPPER_KEYS.PROOF]: 0,
  });
  const [progress, setProgress] = useState({
    [STEPPER_KEYS.ADDITIONAL_INFORMATION]: {},
    [STEPPER_KEYS.PROOF]: {},
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [active]);

  // Update disabled state and fields when payment conditions change
  useEffect(() => {
    if (!data?.subscription) return;

    const subscription = data.subscription;

    // Recalculate payment conditions
    const payment = get(subscription, "payment");
    const receiverId = get(payment, "receiver.id");
    const payerId = get(payment, "payer.id");
    const isPayerReceiver = receiverId === payerId;
    const payerIsAdherent = get(payment, "payer.owner.isAdherent", true);
    const receiverIsAdherent = get(payment, "receiver.owner.isAdherent", true);

    const requiresSubscriberPaysButDifferentReceiver =
      payerIsAdherent && !receiverIsAdherent && !isPayerReceiver;
    const requiresThirdPartyPayer = !payerIsAdherent;

    const requiresBothThirdParty =
      !payerIsAdherent && !receiverIsAdherent && !isPayerReceiver;
    const requiresTwoSeparateRibs = !isPayerReceiver;
    const showAdherentCin =
      requiresSubscriberPaysButDifferentReceiver ||
      requiresThirdPartyPayer ||
      requiresBothThirdParty;

    // Update disabled state for proof fields
    setDisabled((prev) => ({
      ...prev,
      proof: {
        ...prev.proof,
        payerCin: payerIsAdherent,
        refundAccount: isPayerReceiver,
        receiverCin: receiverIsAdherent && !requiresBothThirdParty,
      },
    }));

    // Update fields configuration
    setInitialValues((prev) => {
      if (!prev?.fields) return prev;

      const children = filter(
        get(subscription, "project.contact.relatedPersons", []),
        { relationship: RELATIONSHIP.CHILD },
      );
      const hasSpouse = !isEmpty(
        get(subscription, "project.contact.relatedPersons", []).find(
          (person) => person?.relationship === RELATIONSHIP.SPOUSE,
        ),
      );
      const acceptTeletransmission = get(
        subscription,
        "project.contact.fields.teletransmission",
        true,
      );
      const isTNS = get(subscription, "insurancePlan.fields.tns");
      const isRetired =
        get(subscription, "project.contact.socioProfessionalCategory") ===
        SOCIO_PROFESSIONAL_CATEGORIES.RETIREES;
      const additionalDocuments =
        get(subscription, "insurancePlan.config.additionalDocuments", []) || [];
      const isPensionFundRequired =
        additionalDocuments.includes("proof.pensionFund") && isRetired;
      const isCommunityDocumentRequired =
        additionalDocuments.includes("proof.attachmentOfCommunity") &&
        !isRetired;
      const isLumineis =
        get(subscription, "insurancePlan.product") ===
        MILTIS_PRODUCT_CODES.LUMINEIS;

      // Determine professional situation conditions
      const professionalSituation = get(
        subscription,
        "project.contact.fields.professionalSituation",
      );
      const profession = get(subscription, "project.contact.profession");
      const isPublicServiceEmployee =
        profession ===
        SOCIO_PROFESSIONAL_CATEGORIES.EXECUTIVES_AND_PUBLIC_SERVICE_EMPLOYEES;
      const isActive = professionalSituation === JOB_LIST.ACTIVE;
      const isRetiredProfessional = professionalSituation === JOB_LIST.RETIRED;
      const requiresTerritorialCollectivityAttestation =
        isActive && isPublicServiceEmployee;
      const requiresCnraclIrcantecAffiliation =
        isRetiredProfessional && isPublicServiceEmployee;

      const newProofFields = {
        // Social Security Certificate - only if teletransmission accepted
        ...(acceptTeletransmission && {
          [SUBSCRIPTION_FORM_KEYS.PROOF.SOCIAL_SECURITY_CERTIFICATE]: [
            "proof.socialSecurityCertificate",
            ...(hasSpouse ? ["proof.spouseSocialSecurityCertificate"] : []),
            ...children.map((child, index) => {
              const userId = get(child, "identity.user.id");
              return userId
                ? `proof.childSocialSecurityCertificate_${userId}`
                : `proof.childSocialSecurityCertificate_${index}`;
            }),
          ],
        }),
        // ID documents
        [SUBSCRIPTION_FORM_KEYS.PROOF.ID]: [
          ...(showAdherentCin ? ["proof.cin"] : []),
          ...(requiresThirdPartyPayer || requiresBothThirdParty
            ? ["proof.payerCin"]
            : []),
          ...(requiresSubscriberPaysButDifferentReceiver ||
          requiresBothThirdParty
            ? ["proof.receiverCin"]
            : []),
        ],
        // Bank Account
        [SUBSCRIPTION_FORM_KEYS.PROOF.BANK_ACCOUNT]: requiresTwoSeparateRibs
          ? ["proof.paymentAccount", "proof.refundAccount"]
          : ["proof.paymentAccount"],
        // Proof of Address - not requested for LUMINEIS
        ...(!isLumineis && {
          [SUBSCRIPTION_FORM_KEYS.PROOF.PROOF_OF_ADDRESS]: [
            "proof.proofOfAddress",
          ],
        }),
        // KBIS - only for TNS
        ...(isTNS && {
          [SUBSCRIPTION_FORM_KEYS.PROOF.KBIS]: ["proof.kbis"],
        }),
        // Pension Fund
        ...(isPensionFundRequired && {
          [SUBSCRIPTION_FORM_KEYS.PROOF.PENSION_FUND]: ["proof.pensionFund"],
        }),
        // Community Attachment
        ...(isCommunityDocumentRequired && {
          [SUBSCRIPTION_FORM_KEYS.PROOF.COMMUNITY_ATTACHMENT]: [
            "proof.attachmentOfCommunity",
          ],
        }),
        // Territorial Collectivity Attestation - required when "En activité" AND profession is public service
        ...(requiresTerritorialCollectivityAttestation && {
          [SUBSCRIPTION_FORM_KEYS.PROOF.TERRITORIAL_COLLECTIVITY_ATTESTATION]: [
            "proof.territorialCollectivityAttestation",
          ],
        }),
        // CNRACL/IRCANTEC Affiliation - required when "Retraité" AND profession is public service
        ...(requiresCnraclIrcantecAffiliation && {
          [SUBSCRIPTION_FORM_KEYS.PROOF.CNRACL_IRCANTEC_AFFILIATION]: [
            "proof.cnraclIrcantecAffiliation",
          ],
        }),
      };

      return {
        ...prev,
        fields: {
          ...prev.fields,
          [STEPPER_KEYS.PROOF]: newProofFields,
        },
      };
    });
  }, [
    data?.subscription?.payment?.payer?.id,
    data?.subscription?.payment?.receiver?.id,
    data?.subscription?.payment?.payer?.owner?.isAdherent,
    data?.subscription?.payment?.receiver?.owner?.isAdherent,
    data?.subscription?.project?.contact?.relatedPersons,
    data?.subscription?.project?.contact?.fields?.teletransmission,
    data?.subscription?.project?.contact?.socioProfessionalCategory,
    data?.subscription?.insurancePlan?.fields?.tns,
    data?.subscription?.insurancePlan?.config?.additionalDocuments,
    data?.subscription?.project?.contact?.fields?.professionalSituation,
    data?.subscription?.project?.contact?.profession,
  ]);

  // Recalculate progress when disabled state or initialValues.fields change
  useEffect(() => {
    if (!initialValues?.fields?.[STEPPER_KEYS.PROOF]) return;

    // Get current proof values from the Proof component's state
    const proofValues = initialValues[STEPPER_KEYS.PROOF];
    if (proofValues) {
      // Recalculate progress for all proof blocks
      Object.keys(initialValues.fields[STEPPER_KEYS.PROOF]).forEach((block) => {
        const blockProgress = (() => {
          if (
            disabled[block] &&
            block !== SUBSCRIPTION_FORM_KEYS.PROOF.SOCIAL_SECURITY_CERTIFICATE
          )
            return undefined;

          const fields = initialValues.fields[STEPPER_KEYS.PROOF][block] || [];
          const maxLength = fields.filter((g) => g && !get(disabled, g)).length;
          const filledLength = fields.filter((g) => {
            const value = get(proofValues, g);
            return (
              !isNil(value) &&
              value !== "" &&
              (!isObject(value) || !isEmpty(value)) &&
              !get(disabled, g)
            );
          }).length;
          return maxLength ? Math.ceil((filledLength / maxLength) * 100) : 100;
        })();

        if (blockProgress !== undefined) {
          setProgress((prev) => ({
            ...prev,
            [STEPPER_KEYS.PROOF]: {
              ...prev[STEPPER_KEYS.PROOF],
              [block]: blockProgress,
            },
          }));
        }
      });

      // Recalculate mean progress
      const blocks = Object.keys(initialValues.fields[STEPPER_KEYS.PROOF]);
      const { count, sum } = blocks.reduce(
        (acc, block) => {
          if (
            disabled[block] &&
            block !== SUBSCRIPTION_FORM_KEYS.PROOF.SOCIAL_SECURITY_CERTIFICATE
          )
            return acc;

          const fields = initialValues.fields[STEPPER_KEYS.PROOF][block] || [];
          const maxLength = fields.filter((g) => g && !get(disabled, g)).length;
          const filledLength = fields.filter((g) => {
            const value = get(proofValues, g);
            return (
              !isNil(value) &&
              value !== "" &&
              (!isObject(value) || !isEmpty(value)) &&
              !get(disabled, g)
            );
          }).length;
          const blockProgress = maxLength
            ? Math.ceil((filledLength / maxLength) * 100)
            : 100;

          return { sum: acc.sum + blockProgress, count: acc.count + 1 };
        },
        { count: 0, sum: 0 },
      );

      const meanProgress = count > 0 ? +(sum / count).toFixed(2) : 0;
      setRealProgress((prev) => ({
        ...prev,
        [STEPPER_KEYS.PROOF]: meanProgress,
      }));
    }
  }, [disabled, initialValues?.fields?.[STEPPER_KEYS.PROOF]]);

  const calculateBlockProgress = (parent, block, values, disabled) => {
    if (
      disabled[block] &&
      block !== SUBSCRIPTION_FORM_KEYS.PROOF.SOCIAL_SECURITY_CERTIFICATE
    )
      return;

    let fieldList = Object.values(
      get(initialValues, `fields.${parent}.${block}`, []),
    );

    if (parent === STEPPER_KEYS.ADDITIONAL_INFORMATION) {
      const registeredPaths = new Set(fieldStore.getRegisteredPaths());
      if (registeredPaths.size > 0) {
        fieldList = fieldList.filter((path) => registeredPaths.has(path));
      }
      if (get(values, "project.fields.phoneCanvassing") !== true) {
        fieldList = fieldList.filter(
          (path) => path !== "project.fields.phoneCanvassingConsent",
        );
      }
    }

    const maxLength = fieldList.filter((g) => g && !get(disabled, g)).length;
    const filedLength = fieldList.filter((g) =>
      isBoolean(get(values, g)) && !isNil(get(values, g))
        ? get(values, g) === true
        : !isNil(get(values, g)) &&
          get(values, g) !== "" &&
          (!isObject(get(values, g)) || !isEmpty(get(values, g))) &&
          !get(disabled, g),
    ).length;
    return maxLength ? Math.ceil((filedLength / maxLength) * 100) : 100;
  };

  /** Mean progress for a step; avoids reading stale sibling state from realProgress. */
  const getProgressMean = (parent, stepValues, initialDisabled) => {
    const { count, sum } = Object.keys(
      get(initialValues, `fields.${parent}`, []),
    ).reduce(
      (acc, block) => {
        const blockProgress = calculateBlockProgress(
          parent,
          block,
          stepValues,
          initialDisabled,
        );
        return isNumber(blockProgress)
          ? { sum: (acc.sum += blockProgress), count: (acc.count += 1) }
          : acc;
      },
      { count: 0, sum: 0 },
    );
    return count > 0 ? +(sum / count).toFixed(2) : 0;
  };

  const calculateRealTimeProgress = (
    parent,
    block,
    values,
    initialDisabled = disabled,
  ) => {
    setProgress((prev) => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [block]: calculateBlockProgress(parent, block, values, initialDisabled),
      },
    }));
  };

  const calculateProgressMean = (
    parent,
    values,
    initialDisabled = disabled,
    options = {},
  ) => {
    const { skipPersistence = false } = options;
    const progress = getProgressMean(parent, values, initialDisabled);

    setRealProgress((prev) => ({
      ...prev,
      [parent]: progress,
    }));

    if (skipPersistence) {
      return;
    }

    if (
      parent === STEPPER_KEYS.PROOF &&
      ((progress === 100 && realProgress[parent] !== 100) ||
        (progress !== 100 && realProgress[parent] === 100))
    ) {
      const additionalInfoMean = getProgressMean(
        STEPPER_KEYS.ADDITIONAL_INFORMATION,
        initialValues[STEPPER_KEYS.ADDITIONAL_INFORMATION],
        initialDisabled,
      );
      return updateSubscription({
        variables: {
          where: { id },
          data: {
            additionalInfo: {
              ...get(
                omitDeep(data, "__typename"),
                "subscription.additionalInfo",
                {},
              ),
              isDocumentsCompleted: progress === 100,
              isInfoCompleted: additionalInfoMean === 100,
            },
          },
        },
        onCompleted: () => setIsSubmitting(false),
      });
    }
    if (
      parent === STEPPER_KEYS.ADDITIONAL_INFORMATION &&
      ((progress === 100 && realProgress[parent] !== 100) ||
        (progress !== 100 && realProgress[parent] === 100))
    ) {
      const proofMean = getProgressMean(
        STEPPER_KEYS.PROOF,
        initialValues[STEPPER_KEYS.PROOF],
        initialDisabled,
      );
      return updateSubscription({
        variables: {
          where: { id },
          data: {
            additionalInfo: {
              ...get(
                omitDeep(data, "__typename"),
                "subscription.additionalInfo",
                {},
              ),
              isDocumentsCompleted: proofMean === 100,
              isInfoCompleted: progress === 100,
            },
          },
        },
        onCompleted: () => setIsSubmitting(false),
      });
    }
    setIsSubmitting(false);
  };

  if (loading || error || isEmpty(initialValues) || isEmpty(disabled))
    return <Loading />;

  return (
    <FieldRegistryProvider store={fieldStore}>
      <View
        active={active}
        setActive={setActive}
        data={omitDeep(data, "__typename")}
        progress={progress}
        setProgress={setProgress}
        disabled={disabled}
        setDisabled={setDisabled}
        setPercent={calculateRealTimeProgress}
        realProgress={realProgress}
        calculateProgressMean={calculateProgressMean}
        initialValues={initialValues}
        checkUser={checkUser}
        isSubmitting={isSubmitting}
        setIsSubmitting={setIsSubmitting}
      />
    </FieldRegistryProvider>
  );
};

export default Detail;
